import { ReportStatus } from "../interfaces/ReportStatus";
import { getAwaitingInputRoute } from "../../features/hitl/presentPlan";

export interface ReportRouteFlags {
  coApprovalEnabled: boolean;
  hitlEnabled: boolean;
  /** Viewer holds REPORTS_MANAGE_STATION_PERMISSION (CO perspective). */
  isCoView: boolean;
}

/**
 * The single status→route mapping for opening a report. Union of the rules
 * previously duplicated in ReportCard, the job monitor, and the CO review
 * queue (behavior-preserving — transcribed, not redesigned):
 *
 * - Frozen CO-track: with co-approval OFF, a report stuck in a CO-track
 *   status can't be edited, re-signed, or reviewed — read-only preview so
 *   the owner isn't dropped into an editor whose save is disabled
 *   server-side.
 * - AWAITING_INPUT delegates to the HITL feature's own route fork.
 * - SIGNED / RMS_COMMITTED / GENERATING / unknown → read-only preview.
 *
 * Note: useReportData's editor→preview bounce and ExtractedInfoPage's
 * gating intentionally do NOT use this function — their semantic is "not
 * editable here → show read-only view", not "route to the status home".
 */
export function routeForReport(
  report: { id: string; status: ReportStatus | string },
  flags: ReportRouteFlags,
): string {
  const { id } = report;
  const status = report.status as ReportStatus;
  const preview = `/preview-report-info/${id}`;

  if (
    !flags.coApprovalEnabled &&
    (status === ReportStatus.IN_CO_REVIEW || status === ReportStatus.CHANGES_REQUESTED)
  ) {
    return preview;
  }
  if (status === ReportStatus.PENDING_REVIEW) {
    return `/edit-section/${id}`;
  }
  if (status === ReportStatus.AWAITING_INPUT) {
    return getAwaitingInputRoute(id, flags.hitlEnabled);
  }
  if (status === ReportStatus.CHANGES_REQUESTED) {
    // Officer fixes; CO previews.
    return flags.isCoView ? preview : `/edit-section/${id}`;
  }
  if (
    (status === ReportStatus.IN_CO_REVIEW || status === ReportStatus.CO_APPROVED) &&
    flags.isCoView
  ) {
    return `/co-review/${id}`;
  }
  return preview;
}
